/**
 * Wave D — admin storm detect helpers (ApiClient.core wires these).
 * Fair-queue freeze is driven by latch subscribe in adminOutboundFairQueue.
 */

import { getRetryAfterMs } from 'pi-kiosk-shared';
import {
  getHttpStormRetryAfterMs,
  HTTP_STORM_LATCH_DEFAULT_RETRY_AFTER_MS,
  isHttpStormLatched,
  onHttpStormDetected,
  responseHasHttpStormDetected,
} from './httpStormDetectedLatch.js';

export const HTTP_STORM_LATCHED_CODE = 'HTTP_STORM_LATCHED' as const;

export class HttpStormLatchedError extends Error {
  public readonly code = HTTP_STORM_LATCHED_CODE;
  public readonly status = 429;
  public readonly retryAfterMs: number;

  public constructor(retryAfterMs?: number) {
    const ms =
      retryAfterMs !== undefined && retryAfterMs > 0
        ? retryAfterMs
        : getHttpStormRetryAfterMs() || HTTP_STORM_LATCH_DEFAULT_RETRY_AFTER_MS;
    super(
      `HTTP storm pause active. Retry after ${String(Math.ceil(ms / 1000))}s.`,
    );
    this.name = 'HttpStormLatchedError';
    this.retryAfterMs = ms;
  }
}

export function isHttpStormLatchedError(error: unknown): boolean {
  return error instanceof HttpStormLatchedError;
}

export function assertNotHttpStormLatched(): void {
  if (isHttpStormLatched()) {
    throw new HttpStormLatchedError();
  }
}

export function armHttpStormLatchFromResponse(response: Response): void {
  if (!responseHasHttpStormDetected(response.headers)) {
    return;
  }
  const retryAfterMs = getRetryAfterMs(
    { response },
    HTTP_STORM_LATCH_DEFAULT_RETRY_AFTER_MS,
  );
  onHttpStormDetected(retryAfterMs / 1000);
  // adminOutboundFairQueue subscribeHttpStormLatch → freezeAdminOutboundForStorm(true)
}

/** Drop-in fetch for admin API: honor latch pre-flight, arm on header. */
export async function stormAwareFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  assertNotHttpStormLatched();
  const response = await fetch(input, init);
  armHttpStormLatchFromResponse(response);
  return response;
}
